import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Container, Button, Form, Badge, ButtonGroup } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartLine, 
  faTachometerAlt, 
  faBullseye, 
  faArrowTrendUp,
  faUsers,
  faTruck,
  faBox,
  faClock,
  faFileInvoice,
  faSliders,
  faRefresh,
  faArrowRotateLeft,
  faBroom,
  faEye,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Routes } from '../../router/routes';
import BreadCrumbComponent from '../../components/common/Breadcrumb';
import { useSelector, useDispatch } from 'react-redux';
import EditableDropDown from '../../components/common/EditableDropdown';
import { arrayToDropdownOptions } from '../../utils/sms/arrayToDropdownOptions';
import { returnWarehouseDropdownOptions } from '../../utils/warehouse';
import { updateSpinnerState } from '../../actions/spinner';
import { updateToastInfo } from '../../actions/settings';
import { apiCall } from '../../middlewares/api';
import { apiCallConsts } from '../../constants/apiCallConsts';
import { setStoresInStore } from '../../actions/oms/stores';
import { setCompanyAndCategoryDataInStore } from '../../actions/products';
import { getAllCompaniesAndCategoriesData } from '../../parse-functions/products';
import Pagination from 'react-bootstrap-table2-paginator';
import * as Paginator from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import Table from 'react-bootstrap-table-next';
import { customSizePerPage, customTotal } from '../../components/common/parcelStatusPages';
import EmptyTable from '../../components/common/EmptyTableView';
import { returnUserReadableLocalDateTimeIST } from '../../utils/datetime';
import { Badge as BootstrapBadge } from '@themesberg/react-bootstrap';
import OrderDetails from '../oms/orders/components/OrderDetails';
import exportJsonToExcel from '../../utils/newExcel';

const KPIPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Filter states
  const [openFilter, setOpenFilter] = useState(true);
  const [storeName, setStoreName] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [company, setCompany] = useState('');
  const [productType, setProductType] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');
  const [deliveryType, setDeliveryType] = useState('');
  const [region, setRegion] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Data states
  const [options, setOptions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredKpiData, setFilteredKpiData] = useState(null);
  
  // Table states
  const [selectedBucket, setSelectedBucket] = useState(null); // null = all buckets, 1-4 = specific bucket
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Max stats filter states
  const [selectedMaxFilter, setSelectedMaxFilter] = useState(null); // 'region', 'store', 'status', or null
  
  // Modal states
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  // Redux selectors
  const zones = useSelector((state) => state?.user?.config?.zones || {});
  const storeList = useSelector((state) => state?.stores?.storeList || []);
  const userName = useSelector((state) => state?.user?.user?.username || '');

  // Filter arrays
  const statusArray = [
    { label: 'Assigned', value: 'Assigned' },
    { label: 'Accepted', value: 'Accepted' },
    {label: 'To Be Invoiced', value: 'To Be Invoiced'},
    { label: 'Awaiting Invoice', value: 'Awaiting Invoice' }
    
    // { label: 'Invoiced', value: 'Invoiced' },
    // { label: 'Ready For Pickup', value: 'Ready For Pickup' },
    // { label: 'Picked Up', value: 'Picked Up' },
    // { label: 'Delivered', value: 'Delivered' },
    // { label: 'Pending Action', value: 'Pending Action' },
    // { label: 'Refunded', value: 'Refunded' },
    // { label: 'Failed', value: 'Failed' }
  ];

  // Get all status values for API call when none selected
  const allStatusValues = statusArray.map(option => option.value).join(',');

  const deliveryTypeArray = [
    { label: 'Courier', value: 'courier' },
    { label: 'StorePickup', value: 'storePickup' },
    { label: 'DMS Delivery', value: 'DMSDelivery' },
    { label: 'HyperLocal', value: 'hyperLocal' },
    { label: 'One Hour', value: 'One Hour' },
    // { label: 'DMS Delivery', value: 'DMSDelivery' },
    // { label: 'HyperLocal', value: 'hyperLocal' },
    // { label: 'StorePickup', value: 'storePickup' },
    // { label: 'BuyBack', value: 'buyBack' },
    // { label: 'One Hour', value: 'One Hour' },
    // { label: 'Cashify', value: 'cashify' },
    // { label: 'OBS', value: 'OBS' },
  ];

  const sizeArray = [
    { label: 'Large', value: 'Large' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Small', value: 'Small' },
  ];

  const zoneDropDownOptions = (zones = {}) => {
    const options = [
      { label: 'All Regions', value: 'all' },
      { label: 'NCR', value: 'ncr' },
    ];
    Object.keys(zones).forEach((zone) => {
      options.push({ label: zone, value: zone });
    });
    return options;
  };

  const breadCrumbItems = [
    {
      title: t('Analysis'),
      link: Routes.Analysis.DeliveryData.path,
    },
    {
      title: t('KPI Dashboard'),
      link: '',
    },
  ];

  // Function to filter out orders with missing customer names
  const filterValidOrders = (orders) => {
    if (!orders || !Array.isArray(orders)) {
      return [];
    }
    
    return orders.filter(order => {
      // Check if customerName exists and is not empty/null/undefined
      return order.customerName && 
             order.customerName.trim() !== '' && 
             order.customerName !== null && 
             order.customerName !== undefined;
    });
  };

  // Fetch initial data only once on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        dispatch(updateSpinnerState(true));

        // Fetch stores
        const storeData = await apiCall('get', 'internal/store/all', {});
        dispatch(setStoresInStore(storeData));
        setOptions(returnWarehouseDropdownOptions(storeData, false));

        // Fetch companies and categories
        const categoryData = await getAllCompaniesAndCategoriesData();
        setCompanies(categoryData.companies || []);
        setCategories(categoryData.categories || []);

        // Fetch initial KPI data
        const kpiData = await apiCall(
          apiCallConsts.GET_METHOD,
          'internal/order/filter',
          {
            status: allStatusValues,
          }
        );
        
        // Filter out orders with missing customer names
        const validOrders = filterValidOrders(kpiData?.data || kpiData);
        setFilteredKpiData(validOrders);

        dispatch(updateSpinnerState(false));
      } catch (error) {
        dispatch(updateSpinnerState(false));
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: t('Failed to fetch initial data'),
            message: t(error.message),
          })
        );
        console.error('Error fetching initial data:', error);
      }
    };

    fetchInitialData();
  }, []);

  // Filter functions
  const handleFilter = () => {
    setOpenFilter((prev) => !prev);
  };

  const handleReset = () => {
    setStoreName('');
    setProductCategory('');
    setCompany('');
    setProductType('');
    setCurrentStatus('');
    setDeliveryType('');
    setRegion('');
    setStartDate('');
    setEndDate('');
  };

  const handleEmpty = (setter) => {
    setter('');
  };

  const handleApplyFilter = async () => {
    try {
      dispatch(updateSpinnerState(true));
      
      // Make API call using the same pattern as All Orders
      const response = await apiCall(
        apiCallConsts.GET_METHOD,
        'internal/order/filter',
        {
          storeCode: storeName?.length > 0 ? storeName?.join(',') : storeName,
          categoryName: productCategory?.length > 0 ? productCategory?.join(',') : productCategory,
          companyName: company?.length > 0 ? company?.join(',') : company,
          size: productType?.length > 0 ? productType?.join(',') : productType,
          status: currentStatus?.length > 0 ? currentStatus?.join(',') : allStatusValues,
          deliveryType,
          region: region?.length > 0 ? region?.join(',') : region,
          startDate,
          endDate,
        }
      );

      console.log('KPI Filter Response:', response);

      // Filter out orders with missing customer names and store the filtered data
      const validOrders = filterValidOrders(response?.data || response);
      setFilteredKpiData(validOrders);

      // Show success message
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: t('Success!'),
          message: t('Filters applied successfully'),
        })
      );

      dispatch(updateSpinnerState(false));
    } catch (error) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('Failed to Apply Filters!'),
          message: t(error.message || 'An error occurred while applying filters'),
        })
      );
      console.error('Error applying filters:', error);
    }
  };

  const filterObj = [
    { label: 'STORE NAME', value: storeName, func: setStoreName },
    { label: 'PRODUCT CATEGORY', value: productCategory, func: setProductCategory },
    { label: 'COMPANY', value: company, func: setCompany },
    { label: 'PRODUCT TYPE', value: productType, func: setProductType },
    { label: 'CURRENT STATUS', value: currentStatus, func: setCurrentStatus },
    { label: 'DELIVERY TYPE', value: deliveryType, func: setDeliveryType },
    { label: 'REGION', value: region, func: setRegion },
    { label: 'START DATE', value: startDate, func: setStartDate },
    { label: 'END DATE', value: endDate, func: setEndDate },
  ];

  // Function to convert UTC to IST
  const convertUTCToIST = (utcString) => {
    if (!utcString) return null;
    
    try {
      // Parse the UTC date string
      const utcDate = new Date(utcString);
      
      // Convert to IST (UTC + 5:30)
      const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
      
      return istDate;
    } catch (error) {
      console.error('Error converting UTC to IST:', error);
      return null;
    }
  };

  // Function to categorize orders into time buckets
  const categorizeOrdersByTime = (orders) => {
    if (!orders || !Array.isArray(orders)) {
      return {
        bucket1: [],
        bucket2: [],
        bucket3: [],
        bucket4: [],
        bucket5: [],
        bucket6: []
      };
    }

    const buckets = {
      bucket1: [], // 7:00 PM to 10:00 AM (previous day to current day)
      bucket2: [], // 10:01 AM to 12:30 PM
      bucket3: [], // 12:31 PM to 3:00 PM
      bucket4: [], // 3:01 PM to 7:00 PM
      bucket5: [], // Invoice - Overdue
      bucket6: []  // 1 Hours Delivery / Hyper Local
    };

    // Get current date and time in IST
    const now = new Date();
    const istNow = new Date(now.getTime());
    
    // Calculate time boundaries for bucket 1
    const today1pm = new Date(istNow);
    today1pm.setHours(13, 0, 0, 0); // 1:00 PM today
    
    const yesterday7pm = new Date(istNow);
    yesterday7pm.setDate(yesterday7pm.getDate() - 1);
    yesterday7pm.setHours(19, 0, 0, 0); // 7:00 PM yesterday
    
    const today10am = new Date(istNow);
    today10am.setHours(10, 0, 0, 0); // 10:00 AM today
    
    const today7pm = new Date(istNow);
    today7pm.setHours(19, 0, 0, 0); // 7:00 PM today
    
    const tomorrow10am = new Date(istNow);
    tomorrow10am.setDate(tomorrow10am.getDate() + 1);
    tomorrow10am.setHours(10, 0, 0, 0); // 10:00 AM tomorrow
    
    // Calculate time boundaries for bucket 2
    const today2pm = new Date(istNow);
    today2pm.setHours(14, 0, 0, 0); // 2:00 PM today
    
    const today1230pm = new Date(istNow);
    today1230pm.setHours(12, 30, 0, 0); // 12:30 PM today
    
    // Calculate time boundaries for bucket 3
    const today5pm = new Date(istNow);
    today5pm.setHours(17, 0, 0, 0); // 5:00 PM today
    
    const today3pm = new Date(istNow);
    today3pm.setHours(15, 0, 0, 0); // 3:00 PM today
    
    // Calculate time boundaries for bucket 4
    const today8pm = new Date(istNow);
    today8pm.setHours(20, 0, 0, 0); // 8:00 PM today

    orders.forEach(order => {
      const orderDateTime = order.orderDateTime; // Format: "2025-07-08 04:08:37" (UTC)
      
      if (!orderDateTime) return;

      // 1. Convert UTC orderDateTime to IST
      const orderDateIST = convertUTCToIST(orderDateTime);
      
      if (!orderDateIST) return;

      // Check for 1 Hour Delivery / 3 Hours Delivery / Hyper Local first (Bucket 6)
      const is3HoursDelivery = order.is3HoursDelivery === true || order.is3HoursDelivery === "True";
      const isHyperLocal = order.isHyperLocal === true || order.isHyperLocal === "True";
      const is1HourDelivery = order.orderType === "1 Hour Delivery";
      
      if (is3HoursDelivery || isHyperLocal || is1HourDelivery) {
        buckets.bucket6.push(order);
        return;
      }

      // 2. First Bucket Logic
      let bucket1Start, bucket1End;
      
      if (istNow < today1pm) {
        // If current time is before 1pm today: yesterday 7pm to today 10am
        bucket1Start = yesterday7pm;
        bucket1End = today10am;
      } else {
        // If current time is after 1pm today: today 7pm to tomorrow 10am
        bucket1Start = today7pm;
        bucket1End = tomorrow10am;
      }
      
      if (orderDateIST >= bucket1Start && orderDateIST < bucket1End) {
        buckets.bucket1.push(order);
        return;
      }

      // 3. Second Bucket Logic: 10am to 12:30pm today
      if (orderDateIST >= today10am && orderDateIST <= today1230pm) {
        // If current time is before 2pm today, order goes to bucket 2
        // else it goes to bucket 5
        if (istNow < today2pm) {
          buckets.bucket2.push(order);
        } else {
          buckets.bucket5.push(order);
        }
        return;
      }

      // 4. Third Bucket Logic: 12:30pm to 3pm today
      if (orderDateIST > today1230pm && orderDateIST <= today3pm) {
        // If current time is before 5pm today, order goes to bucket 3
        // else it goes to bucket 5
        if (istNow < today5pm) {
          buckets.bucket3.push(order);
        } else {
          buckets.bucket5.push(order);
        }
        return;
      }

      // 5. Fourth Bucket Logic: 3pm to 7pm today
      if (orderDateIST > today3pm && orderDateIST <= today7pm) {
        // If current time is before 8pm today, order goes to bucket 4
        // else it goes to bucket 5
        if (istNow < today8pm) {
          buckets.bucket4.push(order);
        } else {
          buckets.bucket5.push(order);
        }
        return;
      }

      // All other orders go to bucket 5 (overdue)
      buckets.bucket5.push(order);
    });



    return buckets;
  };

  // Calculate bucket statistics
  const getBucketStats = () => {
    if (!filteredKpiData) {
      return {
        bucket1: { total: 0, toClear: 0 },
        bucket2: { total: 0, toClear: 0 },
        bucket3: { total: 0, toClear: 0 },
        bucket4: { total: 0, toClear: 0 },
        bucket5: { total: 0, toClear: 0 },
        bucket6: { total: 0, toClear: 0 }
      };
    }

    const buckets = categorizeOrdersByTime(filteredKpiData);
    
    return {
      bucket1: { 
        total: buckets.bucket1.length,
      },
      bucket2: { 
        total: buckets.bucket2.length, 
      },
      bucket3: { 
        total: buckets.bucket3.length, 
      },
      bucket4: { 
        total: buckets.bucket4.length, 
      },
      bucket5: { 
        total: buckets.bucket5.length, 
      },
      bucket6: { 
        total: buckets.bucket6.length,
      }
    };
  };

  // Table columns configuration
  const getTableColumns = () => [
    {
      dataField: 'orderNo',
      text: 'Order No',
      sort: true,
      style: { minWidth: '120px', maxWidth: '120px' },
      headerStyle: { minWidth: '120px', maxWidth: '120px' },
    },
    {
      dataField: 'customerName',
      text: 'Customer Name',
      sort: true,
      style: { minWidth: '150px', maxWidth: '150px' },
      headerStyle: { minWidth: '150px', maxWidth: '150px' },
      formatter: (cell) => (
        <div style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          maxWidth: '150px'
        }} title={cell}>
          {cell}
        </div>
      ),
    },
    {
      dataField: 'brand',
      text: 'Brand',
      sort: true,
      style: { minWidth: '100px', maxWidth: '100px' },
      headerStyle: { minWidth: '100px', maxWidth: '100px' },
      formatter: (cell) => (
        <div style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          maxWidth: '100px'
        }} title={cell}>
          {cell || 'N/A'}
        </div>
      ),
    },
    {
      dataField: 'orderType',
      text: 'Order Type',
      sort: true,
      style: { minWidth: '120px', maxWidth: '120px' },
      headerStyle: { minWidth: '120px', maxWidth: '120px' },
      formatter: (cell) => (
        <div style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          maxWidth: '120px'
        }} title={cell}>
          {cell || 'N/A'}
        </div>
      ),
    },
    {
      dataField: 'size',
      text: 'Size',
      sort: true,
      style: { minWidth: '80px', maxWidth: '80px' },
      headerStyle: { minWidth: '80px', maxWidth: '80px' },
      formatter: (cell) => (
        <div style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          maxWidth: '80px'
        }} title={cell}>
          {cell || 'N/A'}
        </div>
      ),
    },
    {
      dataField: 'productTitle',
      text: 'Product Title',
      sort: true,
      style: { minWidth: '200px', maxWidth: '200px' },
      headerStyle: { minWidth: '200px', maxWidth: '200px' },
      formatter: (cell) => (
        <div style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          maxWidth: '200px'
        }} title={cell}>
          {cell || 'N/A'}
        </div>
      ),
    },
    {
      dataField: 'orderDateTime',
      text: 'Order Date',
      formatter: (cell, row) => {
        return returnUserReadableLocalDateTimeIST(cell);
      },
      sort: true,
      style: { minWidth: '140px', maxWidth: '140px' },
      headerStyle: { minWidth: '140px', maxWidth: '140px' },
    },
    {
      dataField: 'status',
      text: 'Status',
      formatter: (cell, row) => {
        const getStatusColor = (status) => {
          switch (status) {
            case 'Awaiting Invoice':
              return 'warning';
            case 'Assigned':
              return 'info';
            case 'Accepted':
              return 'primary';
            default:
              return 'secondary';
          }
        };
        
        return (
          <BootstrapBadge bg={getStatusColor(cell)}>
            {cell}
          </BootstrapBadge>
        );
      },
      sort: true,
      style: { minWidth: '120px', maxWidth: '120px' },
      headerStyle: { minWidth: '120px', maxWidth: '120px' },
    },
    {
      dataField: 'vanNo',
      text: 'VAN No',
      sort: true,
      style: { minWidth: '100px', maxWidth: '100px' },
      headerStyle: { minWidth: '100px', maxWidth: '100px' },
    },
    {
      dataField: 'storeCode',
      text: 'Store',
      sort: true,
      style: { minWidth: '120px', maxWidth: '120px' },
      headerStyle: { minWidth: '120px', maxWidth: '120px' },
      formatter: (cell) => (
        <div style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          maxWidth: '120px'
        }} title={cell}>
          {cell}
        </div>
      ),
    },
    {
      dataField: 'zone',
      text: 'Region',
      sort: true,
      style: { minWidth: '100px', maxWidth: '100px' },
      headerStyle: { minWidth: '100px', maxWidth: '100px' },
      formatter: (cell) => (
        <div style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          maxWidth: '100px'
        }} title={cell}>
          {cell}
        </div>
      ),
    },
    {
      dataField: 'view',
      text: 'View',
      searchable: false,
      style: { minWidth: '60px', maxWidth: '60px' },
      headerStyle: { minWidth: '60px', maxWidth: '60px' },
      formatter: (cell, row) => (
        <FontAwesomeIcon
          icon={faEye}
          style={{ cursor: 'pointer', color: '#007bff' }}
          onClick={() => {
            setSelectedOrderId(row.objectId);
            setShowOrderModal(true);
          }}
        />
      ),
    },
  ];

  // Get filtered table data based on selected bucket
  const getFilteredTableData = () => {
    if (!filteredKpiData) return [];
    
    let filteredData = [];
    

    
    // Apply bucket filter if selected
    if (selectedBucket !== null) {
      const buckets = categorizeOrdersByTime(filteredKpiData);
      const bucketKey = `bucket${selectedBucket}`;
      filteredData = buckets[bucketKey] || [];
    } else {
      // If no bucket selected, show all orders but apply bucketing logic first
      const buckets = categorizeOrdersByTime(filteredKpiData);
      // Combine all buckets for "all orders" view and remove duplicates
      const allOrders = [
        ...buckets.bucket1,
        ...buckets.bucket2,
        ...buckets.bucket3,
        ...buckets.bucket4,
        ...buckets.bucket5,
        ...buckets.bucket6
      ];
      
      // Remove duplicates based on orderNo
      const seenOrders = new Set();
      filteredData = allOrders.filter(order => {
        const orderKey = order.orderNo || order.objectId;
        if (seenOrders.has(orderKey)) {
          return false;
        }
        seenOrders.add(orderKey);
        return true;
      });
    }
    
    // Apply region filter if set
    if (region && region.length > 0) {
      filteredData = filteredData.filter(order => 
        region.includes(order.zone)
      );
    }
    
    // Apply store filter if set
    if (storeName && storeName.length > 0) {
      const storeValues = storeName.map(item => 
        typeof item === 'string' ? item : item.value
      );
      filteredData = filteredData.filter(order => 
        storeValues.includes(order.storeCode)
      );
    }
    
    // Apply status filter if set
    if (currentStatus && currentStatus.length > 0) {
      const statusValues = currentStatus.map(item => 
        typeof item === 'string' ? item : item.value
      );
      filteredData = filteredData.filter(order => 
        statusValues.includes(order.status)
      );
    }
    
    // Apply search filter if set
    if (searchText && searchText.trim() !== '') {
      const searchLower = searchText.toLowerCase();
      filteredData = filteredData.filter(order => 
        (order.orderNo && order.orderNo.toLowerCase().includes(searchLower)) ||
        (order.customerName && order.customerName.toLowerCase().includes(searchLower)) ||
        (order.brand && order.brand.toLowerCase().includes(searchLower)) ||
        (order.orderType && order.orderType.toLowerCase().includes(searchLower)) ||
        (order.size && order.size.toLowerCase().includes(searchLower)) ||
        (order.productTitle && order.productTitle.toLowerCase().includes(searchLower)) ||
        (order.storeCode && order.storeCode.toLowerCase().includes(searchLower)) ||
        (order.status && order.status.toLowerCase().includes(searchLower)) ||
        (order.zone && order.zone.toLowerCase().includes(searchLower))
      );
    }
    
    // Ensure each item has a unique key for the table
    const finalData = filteredData.map((order, index) => ({
      ...order,
      orderNo: order.orderNo || `order-${index}-${Date.now()}`,
      objectId: order.objectId || `order-${index}-${Date.now()}`
    }));
    

    
    return finalData;
  };

  // Handle bucket card click
  const handleBucketClick = (bucketNumber) => {
    if (selectedBucket === bucketNumber) {
      // If clicking the same bucket, show all orders
      setSelectedBucket(null);
    } else {
      // Show only orders from selected bucket
      setSelectedBucket(bucketNumber);
    }
    // Force table refresh
    setTableRefreshKey(prev => prev + 1);
  };

  // Add a state to track search changes
  const [searchText, setSearchText] = useState('');
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Reset to page 1 when filtered data changes (due to search or filters)
  useEffect(() => {
    setCurrentPage(1);
    setTableRefreshKey(prev => prev + 1);
  }, [selectedBucket, selectedMaxFilter, region, storeName, currentStatus, searchText]);

  // Custom search handler with debounce
  const handleSearchChange = (text) => {
    setSearchText(text);
    // Only reset page if we're not on page 1 already
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    // Force table refresh
    setTableRefreshKey(prev => prev + 1);
  };

  // Export function for table data
  const handleExportData = () => {
    const dataToExport = getFilteredTableData();
    
    // Define columns for export
    const exportColumns = [
      { field: 'Order No', key: 'orderNo' },
      { field: 'ItemLineNo', key: 'itemLineNo' },
      { field: 'Customer Name', key: 'customerName' },
      { field: 'Brand', key: 'brand' },
      { field: 'Order Type', key: 'orderType' },
      { field: 'Size', key: 'size' },
      { field: 'Product Title', key: 'productTitle' },
      { field: 'Order Date', key: 'orderDateTime' },
      { field: 'Status', key: 'status' },
      { field: 'VAN No', key: 'vanNo' },
      { field: 'Store', key: 'storeCode' },
      { field: 'Region', key: 'zone' },
    ];

    // Format data for export with proper date formatting
    const formattedData = dataToExport.map(order => ({
      ...order,
      orderDateTime: order.orderDateTime ? returnUserReadableLocalDateTimeIST(order.orderDateTime) : 'N/A',
      brand: order.brand || 'N/A',
      orderType: order.orderType || 'N/A',
      size: order.size || 'N/A',
      productTitle: order.productTitle || 'N/A',
      vanNo: order.vanNo || 'N/A',
      storeCode: order.storeCode || 'N/A',
      zone: order.zone || 'N/A'
    }));

    // Generate filename with current date and time
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
    const fileName = `KPI_Orders_${dateStr}_${timeStr}`;

    // Export the data
    exportJsonToExcel(formattedData, exportColumns, fileName);
  };

  // Calculate max region, store, and status
  const getMaxStats = () => {
    if (!filteredKpiData || !Array.isArray(filteredKpiData)) {
      return {
        maxRegion: { name: 'N/A', count: 0 },
        maxStore: { name: 'N/A', count: 0 },
        maxStatus: { name: 'N/A', count: 0 }
      };
    }

    // Calculate max region
    const regionCounts = {};
    filteredKpiData.forEach(order => {
      const region = order.zone || 'Unknown';
      regionCounts[region] = (regionCounts[region] || 0) + 1;
    });
    const maxRegion = Object.entries(regionCounts).reduce((max, [name, count]) => 
      count > max.count ? { name, count } : max, { name: 'N/A', count: 0 }
    );

    // Calculate max store
    const storeCounts = {};
    filteredKpiData.forEach(order => {
      const store = order.storeCode || 'Unknown';
      storeCounts[store] = (storeCounts[store] || 0) + 1;
    });
    const maxStore = Object.entries(storeCounts).reduce((max, [name, count]) => 
      count > max.count ? { name, count } : max, { name: 'N/A', count: 0 }
    );

    // Calculate max status
    const statusCounts = {};
    filteredKpiData.forEach(order => {
      const status = order.status || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    const maxStatus = Object.entries(statusCounts).reduce((max, [name, count]) => 
      count > max.count ? { name, count } : max, { name: 'N/A', count: 0 }
    );

    return { maxRegion, maxStore, maxStatus };
  };

  // Handle filter clicks for max stats
  const handleMaxRegionClick = () => {
    const maxStats = getMaxStats();
    if (maxStats.maxRegion.name !== 'N/A') {
      if (selectedMaxFilter === 'region') {
        // If clicking the same filter, clear it
        setSelectedMaxFilter(null);
        setRegion([]);
      } else {
        // Set the region filter and clear others
        setSelectedMaxFilter('region');
        setRegion([maxStats.maxRegion.name]);
        setStoreName([]);
        setCurrentStatus([]);
        setSelectedBucket(null);
      }
    }
  };

  const handleMaxStoreClick = () => {
    const maxStats = getMaxStats();
    if (maxStats.maxStore.name !== 'N/A') {
      if (selectedMaxFilter === 'store') {
        // If clicking the same filter, clear it
        setSelectedMaxFilter(null);
        setStoreName([]);
      } else {
        // Set the store filter and clear others
        setSelectedMaxFilter('store');
        setStoreName([maxStats.maxStore.name]);
        setRegion([]);
        setCurrentStatus([]);
        setSelectedBucket(null);
      }
    }
  };

  const handleMaxStatusClick = () => {
    const maxStats = getMaxStats();
    if (maxStats.maxStatus.name !== 'N/A') {
      if (selectedMaxFilter === 'status') {
        // If clicking the same filter, clear it
        setSelectedMaxFilter(null);
        setCurrentStatus([]);
      } else {
        // Set the status filter and clear others
        setSelectedMaxFilter('status');
        setCurrentStatus([maxStats.maxStatus.name]);
        setRegion([]);
        setStoreName([]);
        setSelectedBucket(null);
      }
    }
  };

  // Define kpiCards after all functions are available
  const maxStats = getMaxStats();
  
  const kpiCards = [
    {
      title: 'Max Region',
      subtitle: maxStats.maxRegion.name,
      value: maxStats.maxRegion.count.toString(),
      icon: faTachometerAlt,
      color: selectedMaxFilter === 'region' ? 'dark' : 'primary',
      onClick: handleMaxRegionClick
    },
    {
      title: 'Max Store',
      subtitle: maxStats.maxStore.name,
      value: maxStats.maxStore.count.toString(),
      icon: faBox,
      color: selectedMaxFilter === 'store' ? 'dark' : 'success',
      onClick: handleMaxStoreClick
    },
    {
      title: 'Maximum Status',
      subtitle: maxStats.maxStatus.name,
      value: maxStats.maxStatus.count.toString(),
      icon: faUsers,
      color: selectedMaxFilter === 'status' ? 'dark' : 'warning',
      onClick: handleMaxStatusClick
    }
  ];

  // Get bucket statistics
  const bucketStats = getBucketStats();

  const invoiceSuccessCards = [
    {
      title: 'Invoice - Overdue',
      subtitle: 'Bucket 5',
      value: bucketStats.bucket5.total.toString(),
      color: selectedBucket === 5 ? 'dark' : 'danger',
      ordersToClear: 'OVERDUE-URGENT',
      onClick: () => handleBucketClick(5)
    },
    {
      title: '7:00 PM - 10:00 AM',
      subtitle: 'Bucket 1',
      value: bucketStats.bucket1.total.toString(),
      color: selectedBucket === 1 ? 'dark' : 'primary',
      ordersToClear: (() => {
        const now = new Date();
        const currentHour = now.getHours();
        return currentHour >= 19 ? 'Clear by 1 pm TOMORROW' : 'Clear By 1 PM TODAY';
      })(),
      onClick: () => handleBucketClick(1)
    },
    {
      title: '10:01 AM - 12:30 PM',
      subtitle: 'Bucket 2',
      value: bucketStats.bucket2.total.toString(),
      color: selectedBucket === 2 ? 'dark' : 'success',
      ordersToClear: 'Clear By 2 PM TODAY',
      onClick: () => handleBucketClick(2)
    },
    {
      title: '12:31 PM - 3:00 PM',
      subtitle: 'Bucket 3',
      value: bucketStats.bucket3.total.toString(),
      color: selectedBucket === 3 ? 'dark' : 'warning',
      ordersToClear: 'Clear By 5 PM TODAY',
      onClick: () => handleBucketClick(3)
    },
    {
      title: '3:01 PM - 7:00 PM',
      subtitle: 'Bucket 4',
      value: bucketStats.bucket4.total.toString(),
      color: selectedBucket === 4 ? 'dark' : 'info',
      ordersToClear: 'Clear By 8 PM TODAY',
      onClick: () => handleBucketClick(4)
    },
    {
      title: '3 Hours / Hyper Local',
      subtitle: 'Bucket 6',
      value: bucketStats.bucket6.total.toString(),
      color: selectedBucket === 6 ? 'dark' : 'secondary',
      ordersToClear: 'Hyper Local - IMMEDIATE',
      onClick: () => handleBucketClick(6)
    }
  ];

  return (
    <Container fluid className="px-0">
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <BreadCrumbComponent items={breadCrumbItems} />
          <h1 className="h3">KPI Dashboard</h1>
          <p className="text-muted">
            Key Performance Indicators and Analytics Overview
          </p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <ButtonGroup className="gap-2">
            <Button
              variant={openFilter ? 'primary' : 'white'}
              onClick={handleFilter}
              style={{
                width: '150px',
                height: '38px',
                borderRadius: 0,
              }}
              size="sm"
            >
              <FontAwesomeIcon icon={faSliders} />
              &nbsp;&nbsp;{t('Filter')}
            </Button>
            <Button
              variant="white"
              onClick={async () => {
                try {
                  dispatch(updateSpinnerState(true));
                  const kpiData = await apiCall(
                    apiCallConsts.GET_METHOD,
                    'internal/order/filter',
                    {
                      status: allStatusValues,
                    }
                  );
                  setFilteredKpiData(kpiData?.data || kpiData);
                  dispatch(updateSpinnerState(false));
                  dispatch(
                    updateToastInfo({
                      show: true,
                      type: 'success',
                      title: t('Success!'),
                      message: t('KPI data refreshed successfully'),
                    })
                  );
                } catch (error) {
                  dispatch(updateSpinnerState(false));
                  dispatch(
                    updateToastInfo({
                      show: true,
                      type: 'danger',
                      title: t('Failed to Refresh!'),
                      message: t(error.message || 'An error occurred while refreshing data'),
                    })
                  );
                }
              }}
              style={{
                width: '150px',
                height: '38px',
                borderRadius: 0,
              }}
              size="sm"
            >
              <FontAwesomeIcon icon={faRefresh} />
              &nbsp;&nbsp;{t('Refresh')}
            </Button>
            <Button
              variant="success"
              onClick={handleExportData}
              style={{
                width: '150px',
                height: '38px',
                borderRadius: 0,
              }}
              size="sm"
            >
              <FontAwesomeIcon icon={faDownload} />
              &nbsp;&nbsp;{t('Export')}
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {/* Filter Section */}
      <div
        className="table-settings mt-2 mb-4"
        style={{
          background: '#EAEDF2',
          maxHeight: openFilter ? '700px' : '0px',
          overflow: openFilter ? '' : 'hidden',
          transition: 'max-height 0.7s ease-in-out',
        }}
      >
        <div style={{ gap: 8, display: 'flex' }}>
          <Form.Group
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '20px',
              width: '100%',
              backgroundColor: 'white',
              padding: 20,
            }}
          >
            <div>
              <Form.Label>STORE NAME</Form.Label>
              <EditableDropDown
                id={'StoreName'}
                onChange={(e) => setStoreName(e.map((each) => each.value))}
                style={{
                  height: 38,
                  width: '100%',
                }}
                isMultiSelect={true}
                value={storeName}
                options={options}
                placeholder={'Enter the Store Name'}
              />
            </div>

            <div>
              <Form.Label>PRODUCT CATEGORY</Form.Label>
              <EditableDropDown
                id={'ProductCategory'}
                onChange={(e) => setProductCategory(e.map((each) => each.value))}
                style={{
                  height: 38,
                  width: '100%',
                }}
                isMultiSelect={true}
                value={productCategory}
                options={arrayToDropdownOptions(categories)}
                placeholder={'Enter the Product Category'}
              />
            </div>

            <div>
              <Form.Label>COMPANY</Form.Label>
              <EditableDropDown
                id={'Company'}
                onChange={(e) => setCompany(e.map((each) => each.value))}
                style={{
                  height: 38,
                  width: '100%',
                }}
                value={company}
                isMultiSelect={true}
                options={arrayToDropdownOptions(companies)}
                placeholder={'Enter the Company name'}
              />
            </div>

            <div>
              <Form.Label>PRODUCT TYPE</Form.Label>
              <EditableDropDown
                id={'productType'}
                onChange={(e) => setProductType(e.map((each) => each.value))}
                style={{
                  height: 38,
                  width: '100%',
                }}
                isMultiSelect={true}
                value={productType}
                options={sizeArray}
                placeholder={'Choose the Product Type'}
              />
            </div>

            <div>
              <Form.Label>CURRENT STATUS</Form.Label>
              <EditableDropDown
                id={'CurrentStatus'}
                onChange={(e) => setCurrentStatus(e?.map((each) => each.value))}
                style={{
                  height: 38,
                  width: '100%',
                }}
                isMultiSelect={true}
                value={currentStatus}
                options={statusArray}
                placeholder={'Choose the Current Status'}
              />
            </div>

            <div>
              <Form.Label>DELIVERY TYPE</Form.Label>
              <EditableDropDown
                id={'deliveryType'}
                onChange={(e) => setDeliveryType(e.value)}
                style={{
                  height: 38,
                  width: '100%',
                }}
                value={deliveryType}
                options={deliveryTypeArray}
                placeholder={'Choose the Delivery Type'}
              />
            </div>

            <div>
              <Form.Label>REGION</Form.Label>
              <EditableDropDown
                id={'Region'}
                onChange={(e) => {
                  const values = e.find((option) => option.value === 'all')
                    ? Object.keys(zones)?.map((each) => each)
                    : e.find((option) => option.value === 'ncr')
                    ? ['Delhi', 'Haryana', 'UP']
                    : e?.map((each) => each.value);
                  setRegion(values);
                }}
                style={{
                  height: 38,
                  width: '100%',
                }}
                isMultiSelect={true}
                value={region}
                options={zoneDropDownOptions(zones)}
                placeholder={'Choose the Region'}
              />
            </div>

            <div>
              <Form.Label>START DATE</Form.Label>
              <Form.Control
                style={{
                  height: 38,
                  width: '100%',
                }}
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                placeholder="Start Date"
              />
            </div>

            <div>
              <Form.Label>END DATE</Form.Label>
              <Form.Control
                style={{
                  height: 38,
                  width: '100%',
                }}
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                placeholder="End Date"
              />
            </div>

            <div
              style={{
                gridColumn: 'span 4',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
              }}
            >
              <Button
                variant="white"
                onClick={handleReset}
                style={{
                  width: '150px',
                  height: '38px',
                  borderRadius: 0,
                  background: '#EAEDF2',
                }}
              >
                <FontAwesomeIcon icon={faArrowRotateLeft} />
                &nbsp;&nbsp;{t('Reset')}
              </Button>

              <Button
                variant="white"
                onClick={handleApplyFilter}
                style={{
                  width: '150px',
                  height: '38px',
                  borderRadius: 0,
                  border: '1px solid #262B40',
                }}
              >
                <FontAwesomeIcon icon={faSliders} />
                &nbsp;&nbsp;{t('Apply Filter')}
              </Button>
            </div>
          </Form.Group>
        </div>
      </div>

      {/* Active Filters Display */}
      {!openFilter && (
        (Array.isArray(storeName) && storeName.length > 0) ||
        (Array.isArray(productCategory) && productCategory.length > 0) ||
        (Array.isArray(company) && company.length > 0) ||
        (Array.isArray(productType) && productType.length > 0) ||
        (Array.isArray(currentStatus) && currentStatus.length > 0) ||
        deliveryType !== '' ||
        (Array.isArray(region) && region.length > 0) ||
        startDate !== '' ||
        endDate !== ''
      ) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            className="gap-2"
            style={{
              display: 'flex',
              width: '100%',
              background: '#F5F8FB',
              padding: 20,
            }}
          >
            {filterObj.map((each, index) => {
              // Check if the value exists and is not empty
              const hasValue = Array.isArray(each.value) 
                ? each.value.length > 0 
                : each.value !== '' && each.value !== null && each.value !== undefined;
              
              if (hasValue) {
                // Format the display value
                let displayValue = '';
                if (Array.isArray(each.value)) {
                  displayValue = each.value.join(', ');
                } else {
                  displayValue = each.value;
                }
                
                return (
                  <div key={index}>
                    <Form.Label style={{ color: '#262B4099' }}>
                      {each.label}
                    </Form.Label>
                    <Badge
                      variant="light"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '188px',
                        height: '41px',
                        background: 'white',
                        borderRadius: '2px',
                        cursor: 'pointer',
                        color: '#262B40',
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '16.41px',
                      }}
                    >
                      {displayValue}
                      <span
                        onClick={() => handleEmpty(each.func)}
                        style={{
                          color: '#262B40',
                          fontSize: '14px',
                          fontWeight: '400',
                          lineHeight: '16.41px',
                        }}
                      >
                        &times;
                      </span>
                    </Badge>
                  </div>
                );
              }
              return null;
            })}
          </div>
          <Button
            variant="primary"
            onClick={handleReset}
            style={{
              width: '150px',
              height: '38px',
              borderRadius: 0,
            }}
          >
            <FontAwesomeIcon icon={faBroom} />
            &nbsp;&nbsp;{t('Clear')}
          </Button>
        </div>
      )}

      <Row className="mb-4">
        <Col xs={12}>
          <h4 className="mb-3">Invoice Success Buckets</h4>
        </Col>
      </Row>

      <Row className="mb-4">
        {invoiceSuccessCards.map((card, index) => {
          // Extract bucket number from subtitle (e.g., "Bucket 5" -> 5)
          const bucketNumber = parseInt(card.subtitle.split(' ')[1]);
          
          return (
            <Col key={index} xs={12} sm={6} lg={4} xl={2} className="mb-4">
              <Card 
                className="shadow-sm" 
                style={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease-in-out',
                  transform: card.onClick ? 'scale(1)' : 'scale(1)',
                  border: selectedBucket === bucketNumber ? '3px solid #000000 !important' : '1px solid #e9ecef',
                  boxShadow: selectedBucket === bucketNumber ? '0 6px 20px rgba(0, 86, 179, 0.4)' : '0 2px 4px rgba(0,0,0,0.1)',
                  backgroundColor: selectedBucket === bucketNumber ? '#e3f2fd' : 'white'
                }}
                onMouseEnter={(e) => {
                  if (card.onClick) {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    if (selectedBucket !== bucketNumber) {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                      e.currentTarget.style.border = '1px solid #007bff';
                    }
                  }
                }}
                onMouseLeave={(e) => {
                  if (card.onClick) {
                    e.currentTarget.style.transform = 'scale(1)';
                    if (selectedBucket !== bucketNumber) {
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      e.currentTarget.style.border = '1px solid #e9ecef';
                    }
                  }
                }}
                onClick={card.onClick}
              >
              <Card.Body>
                <Row className="align-items-center">
                  <Col>
                    <div>
                      <h6 className="mb-0">{card.title}</h6>
                      <small className="text-danger">{card.ordersToClear}</small>
                      <h4 className="mb-0 fw-bold">{card.value}</h4>
                      {/* <small className="text-danger">
                        {card.ordersToClear}
                      </small> */}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        );
        })}
      </Row>

      {/* Max Statistics Cards */}
      <Row className="mb-4">
        <Col xs={12}>
          <h4 className="mb-3">Max Statistics</h4>
        </Col>
      </Row>

      <Row className="mb-4">
        {kpiCards.map((card, index) => (
          <Col key={index} xs={12} sm={6} lg={4} className="mb-4">
            <Card 
              className="shadow-sm" 
              style={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                transform: card.onClick ? 'scale(1)' : 'scale(1)',
                border: selectedMaxFilter === ['region', 'store', 'status'][index] ? '3px solid #000000 !important' : '1px solid #e9ecef',
                boxShadow: selectedMaxFilter === ['region', 'store', 'status'][index] ? '0 6px 20px rgba(0, 86, 179, 0.4)' : '0 2px 4px rgba(0,0,0,0.1)',
                backgroundColor: selectedMaxFilter === ['region', 'store', 'status'][index] ? '#e3f2fd' : 'white'
              }}
              onMouseEnter={(e) => {
                if (card.onClick) {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  if (selectedMaxFilter !== ['region', 'store', 'status'][index]) {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                    e.currentTarget.style.border = '1px solid #007bff';
                  }
                }
              }}
              onMouseLeave={(e) => {
                if (card.onClick) {
                  e.currentTarget.style.transform = 'scale(1)';
                  if (selectedMaxFilter !== ['region', 'store', 'status'][index]) {
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    e.currentTarget.style.border = '1px solid #e9ecef';
                  }
                }
              }}
              onClick={card.onClick}
            >
              <Card.Body>
                <Row className="align-items-center">
                  <Col className="col-auto">
                    <div className={`icon-shape icon-shape-${card.color} rounded me-3`}>
                      <FontAwesomeIcon 
                        icon={card.icon} 
                        className="icon icon-sm"
                      />
                    </div>
                  </Col>
                  <Col>
                    <div>
                      <h6 className="mb-0">{card.title}</h6>
                      <small className="text-muted">{card.subtitle}</small>
                      <h4 className="mb-0 fw-bold">{card.value}</h4>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Orders Table */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    Orders {
                      selectedBucket ? `- Bucket ${selectedBucket}` :
                      selectedMaxFilter === 'region' ? `- Max Region (${region[0]})` :
                      selectedMaxFilter === 'store' ? `- Max Store (${storeName[0]})` :
                      selectedMaxFilter === 'status' ? `- Max Status (${currentStatus[0]})` :
                      '(All Orders)'
                    }
                  </h5>
                  <small className="text-muted">
                    {getFilteredTableData().length} orders found
                  </small>
                </Col>
                {(selectedBucket || selectedMaxFilter) && (
                  <Col className="text-end">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setSelectedBucket(null);
                        setSelectedMaxFilter(null);
                        setRegion([]);
                        setStoreName([]);
                        setCurrentStatus([]);
                      }}
                    >
                      Show All Orders
                    </Button>
                  </Col>
                )}
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              <ToolkitProvider
                key={`table-${selectedBucket}-${searchText}-${currentPage}-${JSON.stringify(region)}-${JSON.stringify(storeName)}-${JSON.stringify(currentStatus)}-${tableRefreshKey}`}
                keyField="orderNo"
                sort
                data={getFilteredTableData()}
                columns={getTableColumns()}
                wrapperClasses="table-responsive"
              >
                {({ baseProps, searchProps }) => (
                  <Paginator.PaginationProvider
                    pagination={Pagination({
                      custom: true,
                      showTotal: true,
                      alwaysShowAllBtns: true,
                      totalSize: getFilteredTableData().length,
                      paginationTotalRenderer: customTotal,
                      sizePerPageRenderer: customSizePerPage,
                      onPageChange: handlePageChange,
                      page: currentPage,
                    })}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <>
                        <div className="table-responsive py-2">
                          <div className="px-3 py-2">
                            <Row className="d-flex justify-content-end">
                              <Col xs={12} md={4} className="d-flex justify-content-md-end">
                                <Form.Control
                                  type="text"
                                  placeholder={t('Search orders...')}
                                  value={searchText}
                                  onChange={(e) => handleSearchChange(e.target.value)}
                                  style={{ width: '200px' }}
                                />
                                {searchText && (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => handleSearchChange('')}
                                    style={{ marginLeft: '8px' }}
                                  >
                                    Clear
                                  </Button>
                                )}
                              </Col>
                            </Row>
                          </div>
                          <Table
                            noDataIndication={<EmptyTable />}
                            {...baseProps}
                            {...paginationTableProps}
                            bodyClasses="border-0"
                            classes="table-flush dataTable-table"
                          />
                          <div className="px-3 py-2">
                            <Row>
                              <Col xs={12} md={4} className="py-1">
                                <Paginator.SizePerPageDropdownStandalone
                                  {...paginationProps}
                                />
                              </Col>
                              <Col xs={12} md={4} className="d-flex align-items-center py-1">
                                <Paginator.PaginationTotalStandalone
                                  {...paginationProps}
                                />
                              </Col>
                              <Col xs={12} md={4} className="d-flex justify-content-md-end align-items-center mb-0 py-1">
                                <Paginator.PaginationListStandalone
                                  {...paginationProps}
                                />
                              </Col>
                            </Row>
                          </div>
                        </div>
                      </>
                    )}
                  </Paginator.PaginationProvider>
                )}
              </ToolkitProvider>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* <Row>
        <Col xs={12} lg={8} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                Performance Trends
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                <div className="text-center text-muted">
                  <FontAwesomeIcon icon={faChartLine} size="3x" className="mb-3" />
                  <h6>Performance Trends Chart</h6>
                  <p className="mb-0">Chart visualization will be implemented here</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={12} lg={4} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faBullseye} className="me-2" />
                Goals & Targets
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Delivery Success Rate</span>
                  <span>94.2% / 95%</span>
                </div>
                <div className="progress" style={{ height: '8px' }}>
                  <div className="progress-bar bg-success" style={{ width: '94.2%' }}></div>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Customer Satisfaction</span>
                  <span>4.8 / 5.0</span>
                </div>
                <div className="progress" style={{ height: '8px' }}>
                  <div className="progress-bar bg-primary" style={{ width: '96%' }}></div>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Revenue Target</span>
                  <span>₹12.4M / ₹15M</span>
                </div>
                <div className="progress" style={{ height: '8px' }}>
                  <div className="progress-bar bg-warning" style={{ width: '82.7%' }}></div>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Vehicle Utilization</span>
                  <span>156 / 180</span>
                </div>
                <div className="progress" style={{ height: '8px' }}>
                  <div className="progress-bar bg-info" style={{ width: '86.7%' }}></div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                KPI Summary
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
                <div className="text-center text-muted">
                  <FontAwesomeIcon icon={faBullseye} size="2x" className="mb-3" />
                  <h6>KPI Summary Table</h6>
                  <p className="mb-0">Detailed KPI metrics and comparisons will be displayed here</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row> */}

      {/* Order Details Modal */}
      {showOrderModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              width: '100%',
              height: '100%',
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '20px',
                borderBottom: '1px solid #e9ecef',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f8f9fa'
              }}
            >
              <h4 className="mb-0">Order Details</h4>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setShowOrderModal(false);
                  setSelectedOrderId(null);
                }}
                style={{
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </Button>
            </div>
            
            {/* Modal Body */}
            <div
              style={{
                flex: 1,
                overflow: 'auto',
                padding: '20px'
              }}
            >
              <OrderDetails id={selectedOrderId} />
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default KPIPage; 